import React from 'react';
import { Box, Typography } from '@mui/material';
import { ATTR } from '../../help/attrs';
import { HERO_CARD_SX } from './heroStyles';

export interface HeroShellProps {
    title: React.ReactNode;
    subtitle?: React.ReactNode;
    /** Buttons or links at the right of the title row; they wrap below it on narrow screens. */
    actions?: React.ReactNode;
    /** The one big visual (e.g. the garment silhouette and gauge). */
    visual: React.ReactNode;
    /** Detail card that floats over the visual's right side, and docks below it under 600px. */
    card?: React.ReactNode;
    children?: React.ReactNode;
    /** Help key of the whole page. */
    'data-help-page'?: string;
    /** Help key of the hero area (the big visual and its card). */
    'data-help-section'?: string;
}

/**
 * The soft page frame for hero pages: a calm title row, one big visual area in a rounded card with a
 * floating detail card, then the page's own sections below. 16px side gutters on phones. Desktop
 * spacing follows the app's other pages; nothing here touches the global theme.
 */
export default function HeroShell({ title, subtitle, actions, visual, card, children, 'data-help-section': heroHelp, ...rest }: HeroShellProps) {
    return (
        <Box {...rest} sx={{ px: { xs: 2, sm: 3 }, py: { xs: 2, sm: 3 }, maxWidth: 1400, mx: 'auto' }}>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 1, mb: 2 }}>
                <Box sx={{ flex: '1 1 240px', minWidth: 0 }}>
                    <Typography variant="h5" component="h1" sx={{ overflowWrap: 'anywhere' }}>{title}</Typography>
                    {subtitle && <Typography variant="body2" color="text.secondary">{subtitle}</Typography>}
                </Box>
                {actions && <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>{actions}</Box>}
            </Box>
            <Box
                data-testid="hero-shell-hero"
                {...(heroHelp ? { [ATTR.section]: heroHelp } : {})}
                sx={{
                    ...HERO_CARD_SX, position: 'relative', mb: 2,
                    // the floating card needs room on the right from 600px up
                    pr: card ? { xs: 1.5, sm: '336px' } : HERO_CARD_SX.p,
                    minHeight: { sm: 260 },
                    display: 'flex', flexDirection: 'column', justifyContent: 'center',
                }}
            >
                <Box sx={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', justifyContent: 'center', gap: 3 }}>
                    {visual}
                </Box>
                {card && (
                    <Box
                        data-testid="hero-shell-card"
                        sx={{
                            position: { xs: 'static', sm: 'absolute' }, top: { sm: 16 }, right: { sm: 16 },
                            width: { xs: 'auto', sm: 304 }, mt: { xs: 2, sm: 0 },
                            bgcolor: 'background.paper', border: 1, borderColor: 'divider', borderRadius: 3, p: 2,
                            boxShadow: { xs: 'none', sm: '0 1px 2px rgba(30,25,15,.05), 0 6px 18px rgba(30,25,15,.08)' },
                        }}
                    >
                        {card}
                    </Box>
                )}
            </Box>
            {children}
        </Box>
    );
}
